import { CxfEvents, ILoginData } from '@goodgamestudios/cxf-events'
import { CXF } from '@goodgamestudios/cxf-runtime'
import {
  createLeadingEdgeThrottle,
  loadScript,
  log,
  transformPaymentContractIdsToNativePriceEvent,
} from './helpers'
import { createProductIdsForPlatform, detectPlatform } from './platform'
import { CanvasAppEvent, CanvasEventPostMessage, PushMessageData } from './types'

// A flag to track the listener's state
let isWindowMessageListenerAttached = false

let state = {} as {
  zoneId?: string
}

// Load Canvas Agent during plugin initialization
loadScript(CANVAS_AGENT_URL)
  .then(() => {
    log('Canvas agent loaded')
  })
  .catch((e: any) => {
    console.error('Canvas agent load error', e)
  })

require('@goodgamestudios/cxf-ready').then((cxf: CXF) => {
  let canvasInstance: any
  const eventListener = (event: CanvasEventPostMessage) => {
    log(`Canvas event "${event.eventType}" received:`, event)

    switch (event.eventType) {
      case CanvasAppEvent.CLOSE:
        window.postMessage(
          {
            name: 'cxf.dialog.close',
          },
          '*',
        )
        break
      case CanvasAppEvent.EXPIRED:
        // Canvas session expired, we can choose to refresh the canvas or navigate user to a specific page
        // For demo purposes, we will just refresh the canvas
        window.CanvasAgent?.instance?.refresh()
        break
      case CanvasAppEvent.NATIVE_CATALOG_REQUEST:
        // Canvas Agent requested all payment contract IDs
        if (event?.paymentContractIds?.length > 0) {
          if (!isWindowMessageListenerAttached) {
            window.addEventListener('message', ({ data }: MessageEvent) => {
              if (
                data?.eventName === 'lemonstand.category.update' &&
                data?.data?.action === 'nativePriceUpdate'
              ) {
                // Native client provided back to us native prices for payment contracts
                log('lemonstand.category.update event', data.data)
                if (!data.data.data) return
                const nativeCatalog: Record<string, { amount: number; currency: string }> = {}
                for (const key in data.data.data) {
                  const { priceString, ...rest } = data.data.data[key]
                  nativeCatalog[key] = rest
                }
                log('nativeCatalog:', nativeCatalog)
                window.CanvasAgent?.instance?.setNativeCatalog(nativeCatalog)
              }
            })
            isWindowMessageListenerAttached = true
          }
          // Send payment contract IDs to native client
          const msg = transformPaymentContractIdsToNativePriceEvent(event.paymentContractIds)
          window.postMessage(msg, '*')
          log('Event `lemonstand.nativePrice` sent')
        }
        break
      case CanvasAppEvent.NATIVE_CHECKOUT_REQUEST:
        const { paymentContract, sessionId, offerId, distributionPlatform } = event
        const { zoneId } = state
        window.postMessage(
          {
            eventName: 'lemonstand.nativePay',
            eventType: 'bubble',
            nativePayInfo: {
              pkg: {
                price: paymentContract?.price?.amount,
                productIdMS: '',
                productIds: createProductIdsForPlatform(distributionPlatform, paymentContract?.id),
                tier: Number(paymentContract?.id?.split('_')?.at(-1)),
              },
              meta: {
                criteria: window.CanvasAgent?.instance?.getState()?.appConfig?.canvasAppId,
                lemonstand: true,
                offerId,
                sid: sessionId,
                type: 'canvas', // IMPORTANT: type must be 'canvas'
                zoneId,
                successUrl: window.location.href,
              },
            },
          },
          '*',
        )
        log('Event `lemonstand.nativePay` sent')
        break
      default:
        break
    }
  }

  // Use a leading edge throttling for the `Login` event due to this event can be emitted more than once (during 1-3 secs window)
  const throttledLoginHandler = createLeadingEdgeThrottle((e: ILoginData) => {
    log('Login event')

    if (typeof window.CanvasAgent?.create === 'function') {
      state = { ...state, zoneId: e.zoneId }

      const platform = detectPlatform()
      const canvasApps: string | Record<string, string> = CANVAS_APP_ID_BY_GAME[e.gameId]
      const canvasAppId =
        typeof canvasApps === 'string'
          ? canvasApps
          : (canvasApps as Record<string, string>)[platform]

      if (!canvasAppId) {
        console.error('Canvas app id not found for platform', { platform, canvasApps })
        return
      }
      // Destroy existing Canvas instance
      window.CanvasAgent?.instance?.destroy()

      window.CanvasAgent.create({
        authClientId: 'lemonstand',
        canvasAppId,
        eventListener,
        locale: e.language,
        startupTarget: 'customized',
        token: e.token,
      })
        .then((res) => {
          canvasInstance = res
          window.CanvasAgent.instance = res
        })
        .catch((e) => {
          console.error('Cannot create instance of Canvas', e)
        })
    }
  }, 3000)

  // Register postMessage handlers
  window.addEventListener('message', ({ data }: MessageEvent) => {
    if (data?.name === 'cxf.webshop.sales.open') {
      log('OnOpenSalesOffersPage')
      window.CanvasAgent?.instance?.navigate('supersale')
    }
  })

  cxf.on(CxfEvents.Login, throttledLoginHandler)

  const canvasRefresh = () => {
    log('Game event changed, refreshing canvas')
    window.CanvasAgent?.instance?.refresh()
  }

  cxf.on(CxfEvents.GameEventAdd, canvasRefresh)
  cxf.on(CxfEvents.GameEventUpdate, canvasRefresh)
  cxf.on(CxfEvents.GameEventRemove, canvasRefresh)
  cxf.on(CxfEvents.LevelChanged, canvasRefresh)

  cxf.on(CxfEvents.OpenIGS, (msg: { page?: string } = {}) => {
    log('Open Canvas event')
    // if (window.AirBridge) {
    //   // required for E4K app to open webview for canvas
    //   window.AirBridge.evoke('show()')
    // }
    if (msg?.page) {
      canvasInstance?.navigate(msg.page)
    }
    canvasInstance?.show()
  })

  cxf.on(CxfEvents.Push, async (msg: PushMessageData) => {
    if (msg?.id === 'reward' || msg?.id === 'reward_received') {
      log('OnReward event')
      canvasInstance?.hide()
      canvasInstance?.refresh()
      canvasInstance?.navigate()
    }
  })

  cxf.on(CxfEvents.Token, (token: string ) => {
    log('Token event')
    if (token) {
      window.CanvasAgent?.instance?.authenticate(token)
    }
  })
})
